/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 1st 2022, 10:10:10 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { FixedListAutoComplete, FixedListAutoCompleteProps } from "./FixedListAutoComplete";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";

type OnCloseReason = "toggleInput" | "escape" | "selectOption" | "removeOption" | "blur"; //https://mui.com/material-ui/api/autocomplete/

export interface ListButtonAutoCompleteProps<TOption, Multiple extends boolean>
  extends Omit<
    FixedListAutoCompleteProps<TOption, Multiple>,
    "renderOptionWithDispatch" | "open" | "onOpen" | "onClose"
  > {
  getPrimaryOptionLabel: (option: TOption) => React.ReactNode;
  getSecondaryOptionLabel: (option: TOption) => React.ReactNode;
  disableCloseOnBlur?: boolean;
}

export const ListButtonAutoComplete = <TOption, Multiple extends boolean = false>(
  props: ListButtonAutoCompleteProps<TOption, Multiple>
) => {
  const [open, setOpen] = React.useState(false);

  return (
    <FixedListAutoComplete
      textFieldProps={props.textFieldProps}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={(event: React.SyntheticEvent, reason: OnCloseReason) => {
        if (props?.disableCloseOnBlur && reason === "blur") return; // https://mui.com/material-ui/api/autocomplete/
        setOpen(false);
      }}
      renderOption={(optionProps, option, state) => {
        const handleSelect = () => {
          setOpen(false);

          if (state.selected) return;

          props.onChangeCallBack?.(
            (props.multiple ? [...(props.value as unknown as TOption[]), option] : option) as any
          );
        };

        return (
          <ListItemButton selected={state.selected} onClick={handleSelect}>
            <ListItemText
              primary={props.getPrimaryOptionLabel?.(option)}
              secondary={props.getSecondaryOptionLabel?.(option)}
            />
          </ListItemButton>
        );
      }}
      {...props}
    />
  );
};
